import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';
import { ViewIcon, EditIcon, DeleteIcon } from '../components/IconButtons';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';

const AdminUsersIndexPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    job_position: '',
    role: 'unknown',
    email: '',
    active: '1',
    avatarUrl: '',
    avatarFile: null,
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: true // Open by default since we're in a master data page
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchAdminUsers = async () => {
      setLoading(true);
      try {
        let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/admin_users?page=${currentPage}`;
        if (searchQuery && searchQuery.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
        }
        const response = await authFetch(apiUrl);
        if (!response.ok) throw new Error('Failed to fetch admin users');
        const result = await response.json();
        setData(result.data);
        setLastPage(result.last_page);
        setTotal(result.total);
      } catch (err) {
        setNotification({ show: true, message: 'Gagal memuat data admin user: ' + err.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchAdminUsers();
  }, [currentPage, searchQuery]);

  // Get user data for sidebar
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData((prev) => ({
        ...prev,
        avatarFile: files && files[0] ? files[0] : null,
        avatarUrl: files && files[0] ? URL.createObjectURL(files[0]) : '',
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAdd = () => {
    setFormData({ name: '', job_position: '', role: 'unknown', email: '', active: '1', avatarUrl: '', avatarFile: null });
    setShowAddModal(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formDataToSend = new window.FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('job_position', formData.job_position);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('active', formData.active);
      if (formData.avatarFile) {
        formDataToSend.append('avatar', formData.avatarFile);
      }
      const response = await authFetch(`${API_BASE_URL.replace(/\/$/, '')}/api/admin_users`, {
        method: 'POST',
        body: formDataToSend,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Gagal menambah admin user');
      }
      const newUser = await response.json();
      setData((prev) => [...prev, newUser]);
      setShowAddModal(false);
      setNotification({ show: true, message: 'Admin user berhasil ditambahkan!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    let avatarUrl = '';
    if (user.avatar_url) {
      if (user.avatar_url.startsWith('http')) {
        avatarUrl = user.avatar_url;
      } else {
        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
        avatarUrl = `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`;
      }
    }
    setFormData({
      name: user.name || '',
      job_position: user.job_position || '',
      role: user.role || 'unknown',
      email: user.email || '',
      active: user.active || '1',
      avatarUrl,
      avatarFile: null,
    });
    setShowEditModal(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formDataToSend = new window.FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('job_position', formData.job_position);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('active', formData.active);
      if (formData.avatarFile) {
        formDataToSend.append('avatar', formData.avatarFile);
      }
      const response = await authFetch(`${API_BASE_URL.replace(/\/$/, '')}/api/admin_users/${selectedUser.id}`, {
        method: 'POST',
        headers: { 'X-HTTP-Method-Override': 'PUT' },
        body: formDataToSend,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Gagal mengedit admin user');
      }
      const updated = await response.json();
      setData((prev) => prev.map((item) => item.id === selectedUser.id ? updated : item));
      setNotification({ show: true, message: 'Admin user berhasil diupdate!', type: 'success' });
      setShowEditModal(false);
    } catch (err) {
      setNotification({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (row) => {
    setUserToDelete(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL.replace(/\/$/, '')}/api/admin_users/${userToDelete.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Gagal menghapus admin user');
      setData((prev) => prev.filter((item) => item.id !== userToDelete.id));
      setNotification({ show: true, message: 'Admin user berhasil dihapus!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <h1>Daftar Admin User</h1>
          <p>Kelola data admin user di sistem</p>
        </div>
      </PageHeader>

      <DashboardGrid>
        <AdminSidebar
          user={adminData}
          accordionState={accordionState}
          setAccordionState={setAccordionState}
        />
        <MainSection>
          <ActionHeader>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Cari admin user"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            <AddButton onClick={handleAdd}>+ Tambah Admin User</AddButton>
          </ActionHeader>

          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Foto</th>
                  <th>Nama</th>
                  <th>Jabatan</th>
                  
                  <th>Aktif?</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7">Memuat data...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan="7">Tidak ada data admin user.</td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {(() => {
                          const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
                          return row.avatar_url ? (
                            <img src={row.avatar_url.startsWith('http') ? row.avatar_url : `${baseUrl}${row.avatar_url}`} alt={row.name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</div>
                          );
                        })()}
                      </td>
                      <td>{row.name}</td>
                      <td>{row.job_position}</td>
                      

                      <td>{row.active === '1' ? 'Ya' : 'Tidak'}</td>
                      <td>
                        <Button onClick={() => handleView(row)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Lihat">
                          <ViewIcon />
                        </Button>
                        <Button onClick={() => handleEdit(row)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Edit">
                          <EditIcon />
                        </Button>
                        <DeleteButton onClick={() => handleDelete(row)} style={{ padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Hapus">
                          <DeleteIcon />
                        </DeleteButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <PaginationContainer>
              <PaginationInfo>
                Showing {data.length} of {total} entries
              </PaginationInfo>
              <PaginationButtons>
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </PaginationButton>
                <PageInfo>Page {currentPage} of {lastPage}</PageInfo>
                <PaginationButton
                  onClick={() => setCurrentPage(prev => prev < lastPage ? prev + 1 : prev)}
                  disabled={currentPage === lastPage}
                >
                  Next
                </PaginationButton>
              </PaginationButtons>
            </PaginationContainer>
          </TableContainer>
        </MainSection>
      </DashboardGrid>

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
            ×
          </CloseNotificationButton>
        </NotificationToast>
      )}

      {/* Add Medicine Modal */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Tambah Admin User</h2>
              <CloseButton onClick={() => setShowAddModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmitAdd}>
              <FormGroup>
                <Label>Nama</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Foto</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Input
                    type="file"
                    name="avatar"
                    accept="image/*"
                    onChange={handleInputChange}
                  />
                  {formData.avatarUrl && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img src={formData.avatarUrl} alt="preview" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ccc' }} />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, avatarUrl: '', avatarFile: null }))}
                        style={{ position: 'absolute', top: 0, right: 0, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontWeight: 'bold', lineHeight: '24px', padding: 0 }}
                        title="Hapus gambar"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                  Maksimal ukuran 2MB. Format: JPG, JPEG, PNG.
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Jabatan</Label>
                <Input
                  type="text"
                  name="job_position"
                  value={formData.job_position}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Role</Label>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="super admin">Super Admin</option>
                  <option value="unknown">Unknown</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Aktif?</Label>
                <Select
                  name="active"
                  value={formData.active}
                  onChange={handleInputChange}
                  required
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </Select>
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowAddModal(false)}>
                  Batal
                </CancelButton>
                <SubmitButton type="submit">
                  Simpan
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal>
          <ModalContent>
            <ModalHeader style={{ background: '#ffeaea', borderRadius: '0.5rem 0.5rem 0 0', borderBottom: '2px solid #e74c3c', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '2rem', color: '#e74c3c', marginRight: 12 }}>⚠️</span>
              <h2 style={{ color: '#e74c3c', margin: 0, fontWeight: 700, fontSize: '1.3rem' }}>Hapus Admin User</h2>
              <CloseButton onClick={() => setShowDeleteModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <p style={{ color: '#b71c1c', fontWeight: 500, marginTop: 20 }}>
              Apakah Anda yakin ingin menghapus admin user{' '}
              <strong>{userToDelete?.name}</strong>?
            </p>
            <p style={{ color: '#b71c1c', fontSize: '0.95rem', marginBottom: 32 }}>Tindakan ini <strong>TIDAK DAPAT</strong> dibatalkan.</p>
            <ButtonGroup>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                Batal
              </CancelButton>
              <DeleteButton onClick={confirmDelete} disabled={loading}>
                {loading ? 'Menghapus...' : 'Hapus'}
              </DeleteButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
      
      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Detail Admin User</h2>
              <CloseButton onClick={() => setShowViewModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <div style={{ padding: '1rem 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                {selectedUser.avatar_url ? (
                  <img 
                    src={selectedUser.avatar_url.startsWith('http') 
                      ? selectedUser.avatar_url 
                      : `${API_BASE_URL.replace(/\/$/, '')}${selectedUser.avatar_url.startsWith('/') ? '' : '/'}${selectedUser.avatar_url}`
                    } 
                    alt={selectedUser.name} 
                    style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', marginRight: '1.5rem', border: '1px solid #ddd' }} 
                  />
                ) : (
                  <div style={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%', 
                    backgroundColor: '#e0e0e0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    marginRight: '1.5rem',
                    fontSize: '2rem',
                    color: '#757575'
                  }}>
                    {selectedUser.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>{selectedUser.name}</h3>
                  <p style={{ margin: '0 0 0.25rem 0', color: '#666' }}>{selectedUser.job_position}</p>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#555' }}>Informasi Kontak</h4>
                <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <div style={{ width: '120px', color: '#666' }}>Email:</div>
                  <div style={{ fontWeight: 500 }}>{selectedUser.email}</div>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#555' }}>Informasi Akun</h4>
                <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <div style={{ width: '120px', color: '#666' }}>Role:</div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedUser.role === 'super admin' ? 'Super Admin' : selectedUser.role}
                  </div>
                </div>
                <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <div style={{ width: '120px', color: '#666' }}>Status:</div>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      backgroundColor: selectedUser.active === '1' ? '#e3f2fd' : '#ffebee',
                      color: selectedUser.active === '1' ? '#0d47a1' : '#c62828',
                    }}>
                      {selectedUser.active === '1' ? 'Aktif' : 'Tidak Aktif'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#555' }}>Informasi Lainnya</h4>
                <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <div style={{ width: '120px', color: '#666' }}>Dibuat pada:</div>
                  <div>{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                </div>
                <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <div style={{ width: '120px', color: '#666' }}>Diperbarui:</div>
                  <div>{selectedUser.updated_at ? new Date(selectedUser.updated_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                </div>
              </div>
            </div>
            <ButtonGroup>
              <CancelButton onClick={() => setShowViewModal(false)}>
                Tutup
              </CancelButton>
              <Button 
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedUser);
                }}
                style={{ backgroundColor: '#2196f3' }}
              >
                Edit
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
      
      {/* Edit Medicine Modal */}
      {showEditModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Edit Admin User</h2>
              <CloseButton onClick={() => setShowEditModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmitEdit}>
              <FormGroup>
                <Label>Nama</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Foto</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Input
                    type="file"
                    name="avatar"
                    accept="image/*"
                    onChange={handleInputChange}
                  />
                  {formData.avatarUrl ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={formData.avatarUrl}
                        alt="preview"
                        style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ccc' }}
                      />
                    </div>
                  ) : null}
                </div>
                <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                  Maksimal ukuran 2MB. Format: JPG, JPEG, PNG.
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Jabatan</Label>
                <Input
                  type="text"
                  name="job_position"
                  value={formData.job_position}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Role</Label>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="super admin">Super Admin</option>
                  <option value="unknown">Unknown</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Aktif?</Label>
                <Select
                  name="active"
                  value={formData.active}
                  onChange={handleInputChange}
                  required
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </Select>
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowEditModal(false)}>
                  Batal
                </CancelButton>
                <SubmitButton type="submit">
                  Simpan Perubahan
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  p {
    color: var(--text-light);
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const AddButton = styled.button`
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;

  &:hover {
    background-color: #219150;
    opacity: 0.95;
  }
`;

const TableContainer = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  th {
    background-color: var(--background-light);
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: var(--background-light);
  }
`;



const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid var(--border);
  color: var(--text);
`;

const DeleteButton = styled.button`
  background-color: #e74c3c !important;
  color: #fff !important;
  font-weight: bold;
  border: 2px solid #c0392b;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s, background-color 0.2s;
  &:hover, &:focus {
    background-color: #c0392b !important;
    outline: 2px solid #e74c3c;
    color: #fff !important;
    opacity: 0.95;
  }
`;


// Notification Toast Styles
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  color: #6c757d;
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  background-color: white;
  color: var(--primary);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: #f5f6fa;
  }
`;

const PageInfo = styled.span`
  color: #6c757d;
`;

export default AdminUsersIndexPage;